import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { Button, ConfigProvider, Input, Select, Switch, Typography } from "antd";
import { CloseOutlined, DownOutlined, PlusOutlined, RightOutlined } from "@ant-design/icons";
import { PresetSelection, PresetType } from "@components/PresetSelection";
import { DEFAULT_VISUALIZER_CONFIG, NEW_VISUALIZER_COLUMN, readVisualizerConfig, summarizeColumns } from "@mappers/notes.mapper.mjs";
import type { VisualizerColumn, VisualizerOptions } from "@mappers/notes.mapper.mjs";
import { getNoteVisualizerDefault, saveNoteVisualizerDefault, saveNoteVisualizerPreset } from "@store/actions/notes.actions";
import type { NotesState } from "@store/reducers/notes.reducer";

const OPTION_SWITCHES: { key: keyof VisualizerOptions; label: string }[] = [
    { key: "hideEmpty", label: "Hide empty rows" },
    { key: "truncateNotes", label: "Show truncated notes" },
];

interface ColumnsEditorProps {
    columns: VisualizerColumn[];
    onChange: (columns: VisualizerColumn[]) => void;
    /** The selected preset. It lives in the Notes tab, so a switch of the view keeps it. */
    presetName: string;
    onPresetNameChange: (name: string) => void;
    options: VisualizerOptions;
    onOptionsChange: (options: VisualizerOptions) => void;
}

export const ColumnsEditor = ({ columns, onChange, presetName, onPresetNameChange, options, onOptionsChange }: ColumnsEditorProps) => {
    const dispatch = useDispatch();
    const { user } = useAuth0();
    const tags = useSelector((state: { notes: NotesState }) => state.notes.tags);
    const attributes = useSelector((state: { notes: NotesState }) => state.notes.attributes);
    const teamPresets = useSelector(({ team }: any) => team.presets);
    // `team.presets` is shared with the other preset pickers and can hold other types until the fetch ends.
    // The memo keeps the list stable, because `PresetSelection` rebuilds its options on each new list.
    const presets = useMemo(() => teamPresets.filter((p: any) => p.type === PresetType.NoteVisualizer), [teamPresets]);
    const [open, setOpen] = useState(false);
    const [unsaved, setUnsaved] = useState(false);

    // A column edit marks the preset as unsaved. A preset load goes straight to `onChange`, so it does not.
    const edit = (next: VisualizerColumn[]) => {
        onChange(next);
        setUnsaved(true);
    };

    const update = (index: number, patch: Partial<VisualizerColumn>) =>
        edit(columns.map((column, i) => (i === index ? { ...column, ...patch } : column)));

    const teamDefault = useSelector((state: { notes: NotesState }) => state.notes.visualizerDefault);
    useEffect(() => {
        dispatch(getNoteVisualizerDefault() as any);
    }, [dispatch]);

    // Save on "Default" saves the team default, as on the other tabs. Save on a preset saves over that preset.
    const handleSave = () => {
        const config = { columns, ...options };
        const preset = presets.find((p: any) => p.name === presetName);
        dispatch((preset ? saveNoteVisualizerPreset(preset, config) : saveNoteVisualizerDefault(config)) as any);
    };

    return (
        <div className="notes-viz-editor">
            <div className="notes-viz-editor-head">
                <Button
                    type="text"
                    size="small"
                    icon={open ? <DownOutlined /> : <RightOutlined />}
                    aria-expanded={open}
                    onClick={() => setOpen(!open)}
                    data-attr="notes.visualizer.toggle-columns"
                >
                    Columns
                </Button>
                <Typography.Text type="secondary" className="notes-viz-editor-summary" title={summarizeColumns(columns)}>
                    {summarizeColumns(columns)}
                </Typography.Text>
                {/* The picker stays mounted while hidden. A new mount reloads the preset and drops unsaved column edits. */}
                <div className="notes-viz-presets" hidden={!open}>
                    {/* The shared picker has no size prop. The small size matches the Columns toggle. */}
                    <ConfigProvider componentSize="small">
                        <PresetSelection
                            presets={presets}
                            user={user!}
                            presetType={PresetType.NoteVisualizer}
                            // The shared picker types its config as a list. A Visualizer preset saves an object.
                            statDefaults={(teamDefault ?? DEFAULT_VISUALIZER_CONFIG) as any}
                            currStatMetrics={{ columns, ...options } as any}
                            setStatMetrics={(config) => {
                                const next = readVisualizerConfig(config);
                                onChange(next.columns);
                                onOptionsChange({ hideEmpty: next.hideEmpty, truncateNotes: next.truncateNotes });
                            }}
                            currentSelectedTilePreset={presetName}
                            setCurrentSelectedTilePreset={onPresetNameChange}
                            handleSave={handleSave}
                            warnUnsavedChanges={unsaved}
                            setWarnUnsavedChages={setUnsaved}
                            saveButtonLabel="Save"
                            width={null}
                        />
                    </ConfigProvider>
                </div>
                {open && (
                    <Button
                        size="small"
                        icon={<PlusOutlined />}
                        className="notes-viz-editor-add"
                        onClick={() => edit([...columns, NEW_VISUALIZER_COLUMN])}
                        data-attr="notes.visualizer.add-column"
                    >
                        Add column
                    </Button>
                )}
            </div>
            {open && (
                <div className="notes-viz-editor-body">
                    {columns.map((column, index) => (
                        // Columns have no id of their own. The list only grows at the end or loses one column, so the index is stable enough.
                        <div key={index} className="notes-viz-editor-row">
                            <Input
                                placeholder="Column name"
                                aria-label="Column name"
                                value={column.name}
                                onChange={(e) => update(index, { name: e.target.value })}
                            />
                            <Select
                                mode="multiple"
                                allowClear
                                maxTagCount="responsive"
                                aria-label="Column tags"
                                placeholder="Tags (all must match)"
                                value={column.tags}
                                options={tags.map((t) => ({ value: t.name }))}
                                onChange={(value: string[]) => update(index, { tags: value })}
                            />
                            <Select
                                allowClear
                                aria-label="Highlight attribute"
                                placeholder="Highlight attribute"
                                value={
                                    attributes.some((a) => a.id === column.highlightAttributeId) ? column.highlightAttributeId : undefined
                                }
                                options={attributes.map((a) => ({ value: a.id, label: a.name }))}
                                onChange={(value?: number) => update(index, { highlightAttributeId: value ?? null })}
                            />
                            <Button
                                type="text"
                                icon={<CloseOutlined />}
                                disabled={columns.length === 1}
                                aria-label="Remove column"
                                title={columns.length === 1 ? "At least one column stays" : "Remove column"}
                                data-attr="notes.visualizer.remove-column"
                                onClick={() => edit(columns.filter((_, i) => i !== index))}
                            />
                        </div>
                    ))}
                    <div className="notes-viz-editor-options">
                        {OPTION_SWITCHES.map(({ key, label }) => (
                            <label key={key} className="notes-viz-option">
                                <Switch
                                    size="small"
                                    checked={options[key]}
                                    onChange={(checked) => {
                                        onOptionsChange({ ...options, [key]: checked });
                                        setUnsaved(true);
                                    }}
                                    data-attr={`notes.visualizer.toggle-${key}`}
                                />
                                <Typography.Text type="secondary">{label}</Typography.Text>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
